import React, { useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import "./order.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const SingleDrmtlgyOrderStatus = () => {
    const [ordersInput, setOrdersInput] = useState("");
    const [currentOrders, setCurrentOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fulfillmentColors = {
        fulfilled: "green",
        partial: "orange",
        unfulfilled: "red",
    };

    const handleClear = () => {
        setOrdersInput("");
        setCurrentOrders([]);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        setCurrentOrders([]);

        const ordersArray = ordersInput
            .split(",")
            .map((num) => num.trim())
            .filter(Boolean);

        if (ordersArray.length > 20) {
            setError("Please enter up to 20 order numbers at once.");
            setLoading(false);
            return;
        }

        try {
            const results = [];
            for (const orderNumber of ordersArray) {
                try {
                    const response = await axios.get(
                        `${API_BASE_URL}/api/drmtlgy/shopify/order-status/${orderNumber}`,
                        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
                    );
                    results.push(response.data.order);
                } catch (err) {
                    results.push({
                        orderNumber,
                        error:
                            err.response?.data?.error ||
                            "Order not found or error fetching order",
                    });
                }
            }
            setCurrentOrders(results);
        } catch (err) {
            setError("Unexpected error occurred while fetching orders.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4">DRMTLGY Single Order Status</h2>
            <form onSubmit={handleSubmit} className="mb-4">
                <div className="mb-3">
                    <label htmlFor="ordersInput" className="form-label">
                        Enter Order Numbers (comma separated, max 20)
                    </label>
                    <textarea
                        id="ordersInput"
                        className="form-control"
                        rows="3"
                        value={ordersInput}
                        onChange={(e) => setOrdersInput(e.target.value)}
                        placeholder="Example: 1001, 1002, 1003"
                    />
                </div>
                <button type="submit" className="btn btn-primary me-2">
                    Check Orders
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClear}
                >
                    Clear
                </button>
            </form>

            {loading && (
                <div className="text-center">
                    <ClipLoader size={35} color={"#123abc"} loading={loading} />
                    <p>Loading orders...</p>
                </div>
            )}

            {error && <div className="alert alert-danger">{error}</div>}

            {!loading && currentOrders.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-striped table-hover">
                        <thead className="table-dark">
                            <tr>
                                <th>ID</th>
                                <th>Store Order No</th>
                                <th>Customer Name</th>
                                <th>City</th>
                                <th>State</th>
                                <th>Zipcode</th>
                                <th>Country</th>
                                <th>Created Date</th>
                                <th>Processed Date</th>
                                <th>Order Item Name</th>
                                <th>Order Item Qty</th>
                                <th>Refunded Item Name</th>
                                <th>Refunded Item Qty</th>
                                <th>Fulfillable Qty</th>
                                <th>Fulfilled Qty</th>
                                <th>Item Fulfillment Status</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th>Fulfillment Status</th>
                                <th>Fulfillment Method</th>
                                <th>Shipment Status</th>
                                <th>Shipper Name</th>
                                <th>Tracking Number</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentOrders.length > 0 ? (
                                currentOrders.flatMap((order) => {
                                    const address =
                                        order.shipping_address ||
                                        order.billing_address ||
                                        order.customer?.default_address ||
                                        {};

                                    const rows = [];

                                    // 🔹 Build refunded qty map for this order
                                    const refundedMap = {};
                                    order.refunds?.forEach((refund) => {
                                        refund.refund_line_items?.forEach((rli) => {
                                            if (rli.line_item_id) {
                                                refundedMap[rli.line_item_id] =
                                                    (refundedMap[rli.line_item_id] || 0) + (rli.quantity || 0);
                                            }
                                        });
                                    });

                                    // 🔹 Normal order items
                                    order.line_items?.forEach((item, idx) => {
                                        // Raw fulfilled qty
                                        const rawFulfilled =
                                            (item.quantity || 0) - (item.fulfillable_quantity || 0);

                                        // Refunded qty for this item
                                        const refundedQty = refundedMap[item.id] || 0;

                                        // ✅ Final fulfilled excludes refunded qty
                                        const fulfilledQty = Math.max(0, rawFulfilled - refundedQty);

                                        // Function to get line item fulfillment status
                                        const getLineItemFulfillmentStatus = () => {
                                            if (fulfilledQty > 0) return "fulfilled";

                                            if (item.fulfillment_status) {
                                                return item.fulfillment_status;
                                            }

                                            if (order.fulfillments?.length) {
                                                for (const fulfillment of order.fulfillments) {
                                                    for (const fItem of fulfillment.line_items || []) {
                                                        if (fItem.id === item.id) {
                                                            return fulfillment.shipment_status || "fulfilled";
                                                        }
                                                    }
                                                }
                                            }
                                            return "unfulfilled";
                                        };

                                        rows.push(
                                            <tr key={`${order.id}-item-${idx}`}>
                                                <td>{order.id}</td>
                                                <td>{order.name || "—"}</td>
                                                <td>{order.customer?.first_name || ""} {order.customer?.last_name || ""}</td>
                                                <td>{address.city || "—"}</td>
                                                <td>{address.province || address.province_code || "—"}</td>
                                                <td>{address.zip || "—"}</td>
                                                <td>{address.country || address.country_code || "—"}</td>
                                                <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td>
                                                <td>{order.processed_at ? new Date(order.processed_at).toLocaleDateString() : "—"}</td>

                                                {/* Order item columns */}
                                                <td>{item.name}</td>
                                                <td>{item.quantity}</td>
                                                <td>—</td> {/* Refunded Item Name */}
                                                <td>{refundedQty}</td> {/* ✅ Refunded Item Qty */}
                                                <td>{item.fulfillable_quantity || 0}</td>
                                                <td>{fulfilledQty}</td> {/* ✅ Fulfilled Qty */}
                                                <td>{getLineItemFulfillmentStatus()}</td>

                                                {/* Order-level columns */}
                                                <td>{order.total_price || "—"}</td>
                                                <td>{order.financial_status || "—"}</td>
                                                <td
                                                    style={{
                                                        color: order.fulfillment_status === "fulfilled" ? "lightgreen" : "inherit",
                                                        fontWeight: order.fulfillment_status === "fulfilled" ? "bold" : "normal",
                                                    }}
                                                >
                                                    {order.fulfillment_status || "unfulfilled"}
                                                </td>
                                                <td>{item.fulfillment_service || "—"}</td>
                                                <td>{order.fulfillments?.[0]?.shipment_status || "—"}</td>
                                                <td>{order.fulfillments?.[0]?.tracking_company || "—"}</td>
                                                <td>{order.fulfillments?.[0]?.tracking_number || "—"}</td>
                                            </tr>
                                        );
                                    });

                                    // 🔹 Refunded items section (separate rows)
                                    order.refunds?.forEach((refund) => {
                                        refund.refund_line_items?.forEach((rli, idx) => {
                                            const refundedItem =
                                                rli.line_item ||
                                                order.line_items?.find((li) => li.id === rli.line_item_id);

                                            let refundedFulfillmentStatus =
                                                refundedItem?.fulfillment_status || "unfulfilled";

                                            rows.push(
                                                <tr key={`${order.id}-refund-${idx}`}>
                                                    <td>{order.id}</td>
                                                    <td>{order.name || "—"}</td>
                                                    <td>{order.customer?.first_name || ""} {order.customer?.last_name || ""}</td>
                                                    <td>{address.city || "—"}</td>
                                                    <td>{address.province || address.province_code || "—"}</td>
                                                    <td>{address.zip || "—"}</td>
                                                    <td>{address.country || address.country_code || "—"}</td>
                                                    <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : "—"}</td>
                                                    <td>{order.processed_at ? new Date(order.processed_at).toLocaleDateString() : "—"}</td>

                                                    {/* Refunded item columns */}
                                                    <td>—</td> {/* Order Item Name */}
                                                    <td>—</td> {/* Order Item Qty */}
                                                    <td>{refundedItem?.name || "—"}</td>
                                                    <td>{rli.quantity || 0}</td>
                                                    <td>—</td> {/* Fulfillable Qty */}
                                                    <td>—</td> {/* Fulfilled Qty */}
                                                    <td>{refundedFulfillmentStatus}</td>

                                                    {/* Order-level columns */}
                                                    <td>{order.total_price || "—"}</td>
                                                    <td>{order.financial_status || "—"}</td>
                                                    <td
                                                        style={{
                                                            color: order.fulfillment_status === "fulfilled" ? "lightgreen" : "inherit",
                                                            fontWeight: order.fulfillment_status === "fulfilled" ? "bold" : "normal",
                                                        }}
                                                    >
                                                        {order.fulfillment_status || "unfulfilled"}
                                                    </td>
                                                    <td>{refundedItem?.fulfillment_service || "—"}</td>
                                                    <td>{order.fulfillments?.[0]?.shipment_status || "—"}</td>
                                                    <td>{order.fulfillments?.[0]?.tracking_company || "—"}</td>
                                                    <td>{order.fulfillments?.[0]?.tracking_number || "—"}</td>
                                                </tr>
                                            );
                                        });
                                    });

                                    return rows;
                                })
                            ) : (
                                <tr>
                                    <td colSpan="23" className="text-center">No orders found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SingleDrmtlgyOrderStatus;
